import { differenceInCalendarDays } from 'date-fns';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { DataTableColumn } from '@components/DataTable/DataTable';
import { DateInput } from '@components/DateInput';
import Dropdown from '@components/Dropdown';
import type { FilterItems } from '@components/FilterButton/FilterButton';
import FilterCheckbox from '@components/FilterCheckbox';
import Icon from '@components/Icon/Icon';

import {
  FilterBy,
  FilterClear,
  FilterOptions,
  Heading,
  ModalWrapper,
  Section,
  SectionCheckbox,
  SectionItem,
  SectionTitle,
} from './FilterModalContent.style';

const messages = defineMessages({
  clearFilters: {
    id: 'filterModalContent.clearFilters',
    defaultMessage: 'Clear Filters',
  },
  filterBy: {
    id: 'filterModalContent.filterBy',
    defaultMessage: 'Filter By',
  },
  select: {
    id: 'GLOBAL.selectFrom',
    defaultMessage: 'Select...',
  },
  yes: {
    id: 'GLOBAL.yes',
    defaultMessage: 'Yes',
  },
  no: {
    id: 'GLOBAL.no',
    defaultMessage: 'No',
  },
});

const FilterModalContent: React.FC<{
  inputCallback?: (event: FilterItems) => void;
  filters: FilterItems;
  fields: DataTableColumn[];
}> = ({ inputCallback, filters, fields }) => {
  const { formatMessage } = useIntl();

  const selectCheckbox = (value: boolean, category: string, option: string) => {
    inputCallback?.({
      date: { ...filters.date },
      boolean: { ...filters.boolean },
      others: {
        ...filters.others,
        [category]: { ...filters.others[category], [option]: value },
      },
    });
  };

  const selectDropdown = (category: string, value: boolean | null) => {
    inputCallback?.({
      date: { ...filters.date },
      others: { ...filters.others },
      boolean: {
        ...filters.boolean,
        [category]: value,
      },
    });
  };

  const clearFilters = () => {
    const clearedFilters = {
      date: { name: filters.date.name, to: null, from: null },
      boolean: { ...filters.boolean },
      others: { ...filters.others },
    };

    Object.values(clearedFilters.others).forEach((category) => {
      Object.keys(category).forEach((option) => {
        category[option] = false;
      });
    });

    if (inputCallback) {
      inputCallback(clearedFilters);
    }
  };

  const lookupTitle = (category: string) => {
    const matchingField = fields.find(
      (f) => f.name === category || f.style === category
    );
    return matchingField ? matchingField.title : category;
  };

  const hasDate = fields.find((f) => f.name === 'date' || f.style === 'date');
  const hasBoolean = fields.find((f) => f.style === 'boolean');

  const setDateRange = (dateFrom: Date | null, dateTo: Date | null) => {
    if (
      inputCallback &&
      !(dateFrom && dateTo && differenceInCalendarDays(dateFrom, dateTo) > 0)
    ) {
      inputCallback({
        others: { ...filters.others },
        boolean: { ...filters.boolean },
        date: {
          ...filters.date,
          from: dateFrom,
          to: dateTo,
        },
      });
    }
  };

  return (
    <ModalWrapper>
      <Heading>
        <FilterBy>
          <Icon
            clickable
            color="text.primary"
            name="sliders"
            style={{ marginRight: 4 }}
          />
          <h2>{formatMessage(messages.filterBy)}</h2>
        </FilterBy>
        <FilterClear onClick={clearFilters}>
          <p className="section-link">{formatMessage(messages.clearFilters)}</p>
        </FilterClear>
      </Heading>

      <FilterOptions>
        {hasDate ? (
          <Section key="section-date">
            <SectionTitle>{lookupTitle('date')}</SectionTitle>
            <div>
              <DateInput
                label="From"
                onChange={(date) => setDateRange(date, filters.date.to)}
                value={filters.date.from}
              />

              <DateInput
                label="To"
                onChange={(date) => setDateRange(filters.date.from, date)}
                value={filters.date.to}
              />
            </div>
          </Section>
        ) : null}

        {hasBoolean
          ? Object.entries(filters.boolean).map(([category, categoryValue]) => {
              const value =
                categoryValue === true ? 1 : categoryValue === false ? 0 : -1;
              return (
                <Section key={`section-${category}`}>
                  <SectionTitle>{lookupTitle(category)}</SectionTitle>
                  <SectionItem key={`filter-section-${category}`}>
                    <SectionCheckbox>
                      <Dropdown
                        onChange={(value: string) => {
                          const numberValue = Number(value);
                          const inputValue =
                            numberValue === 1
                              ? true
                              : numberValue === 0
                                ? false
                                : null;
                          selectDropdown(category, inputValue);
                        }}
                        value={value}
                      >
                        <option value="-1">
                          {formatMessage(messages.select)}
                        </option>
                        <option value="1">{formatMessage(messages.yes)}</option>
                        <option value="0">{formatMessage(messages.no)}</option>
                      </Dropdown>
                    </SectionCheckbox>
                  </SectionItem>
                </Section>
              );
            })
          : null}

        {Object.entries(filters.others).flatMap(([category, categoryValue]) => {
          if (!Object.keys(categoryValue).length) {
            return [];
          }

          return [
            <Section key={`section-${category}`}>
              <SectionTitle>{lookupTitle(category)}</SectionTitle>
              {Object.entries(categoryValue).map(([option, optionValue]) => (
                <SectionItem key={`filter-section-${option}`}>
                  <p>{option}</p>
                  <SectionCheckbox>
                    <FilterCheckbox
                      checked={optionValue}
                      onChange={(e) => {
                        selectCheckbox(e.target.checked, category, option);
                      }}
                    />
                  </SectionCheckbox>
                </SectionItem>
              ))}
            </Section>,
          ];
        })}
      </FilterOptions>
    </ModalWrapper>
  );
};

export default FilterModalContent;
